interface SegmentedOption<T extends string> {
  label: string;
  value: T;
}

interface SegmentedFilterProps<T extends string> {
  label: string;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  value: T;
}

/**
 * A filter, not a tablist. Every button stays tabbable and carries
 * `aria-pressed`, because a filter has no single active "page" to rove to and
 * a visitor may want to reach any option directly.
 *
 * Consumers filter by rendering, never by toggling `hidden` on the grid: the
 * global `[hidden] { display: none !important }` rule would fight the layout,
 * and the default option renders every item on the server for crawlers.
 *
 * A <fieldset> with a visually-hidden <legend> rather than a div with
 * role="group": same announcement, native semantics, and no suppression.
 */
export function SegmentedFilter<T extends string>({
  label,
  onChange,
  options,
  value,
}: Readonly<SegmentedFilterProps<T>>) {
  return (
    <fieldset className="segmented">
      <legend>{label}</legend>
      {options.map((option) => (
        <button
          aria-pressed={option.value === value}
          key={option.value}
          onClick={() => onChange(option.value)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}
